import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useHistory, useLocation } from "react-router-dom";
import { Link } from "react-router-dom";
import { withNamespaces } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { sidebarMenuOptions } from "./SidebarMenuOptions";
import { getProfileRoleInfo, getLicensingData } from "../../store/actions";
import DirtyRedirectCheckHoc from "./../../routes/dirtyRedirectCheckHoc";
import { Box, Typography } from "@mui/material";

const getInitialMenus = () => {
  try {
    const s = sessionStorage.getItem("menus");
    if (s) {
      const arr = JSON.parse(s);
      if (Array.isArray(arr) && arr.length > 0) return arr;
    }
  } catch (e) {}
  return [];
};

const SidebarContent = (props) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [sideMenuOptions, setSideMenuOptions] = useState(getInitialMenus);
  const { profile } = useSelector((state) => state);
  const licenseData = useSelector((state) => state.licensing);
  const depth = 0;
  const depthStep = 10;
  const isCollapsed = props.isCollapsed === true;

  // Collapsed view: sub-options on hover only (opaque flyout). Common patterns: (1) Hover flyout
  // to the right (VS Code, many dashboards); (2) Click accordion below parent. We use (1): hover
  // opens flyout, click sub-option navigates and closes flyout; parent stays highlighted.
  const [hoveredSubmenu, setHoveredSubmenu] = useState(null);
  const hoverTimeoutRef = useRef(null);

  const clearHoverTimeout = () => {
    if (hoverTimeoutRef.current) {
      clearTimeout(hoverTimeoutRef.current);
      hoverTimeoutRef.current = null;
    }
  };

  const openSubmenuFlyout = (item, rect) => {
    clearHoverTimeout();
    setHoveredSubmenu({ item, rect });
  };
  const closeSubmenuFlyout = () => {
    hoverTimeoutRef.current = setTimeout(() => setHoveredSubmenu(null), 150);
  };

  const handleSubmenuLinkClick = (e, entity) => {
    e.preventDefault();
    setHoveredSubmenu(null);
    makeLinkActive(e, entity);
    handleRedirectLink(e, entity);
  };

  // Check if a menu item's link matches current path (for flyout active state)
  const isPathActive = (link) => {
    if (!link) return false;
    const pathname = (location.pathname.replace(/\/$/, "") || "/").toLowerCase();
    const normalizedLink = (link.replace(/\/$/, "") || "/").toLowerCase();
    return pathname === normalizedLink || pathname.startsWith(normalizedLink + "/");
  };

  const makeExpandAll = (menus) => {
    menus.forEach((subMenu) => {
      if (!subMenu.hasOwnProperty("expand")) {
        return;
      }
      if (subMenu.hasOwnProperty("expand")) {
        subMenu.expand = true;
      }
      if (subMenu.subMenu) {
        makeExpandAll(subMenu.subMenu);
      }
    });
    return menus;
  };

  const handleRedirectLink = (e, item = {}) => {
    const isDirty = sessionStorage.getItem("isDirty", "true");
    const targetLink = item.link;
    if (targetLink && !isDirty) {
      e.preventDefault();
      e.stopPropagation();
      history.push(targetLink, { id: item.id, params: item.params });
    }
  };

  const toggleExpandFlag = (menus, item) => {
    menus.forEach((subMenu) => {
      if (subMenu === item && subMenu.hasOwnProperty("expand")) {
        subMenu.expand = !subMenu.expand;
        //                console.log("Flag flipped")
        return subMenu;
      } else if (subMenu.subMenu) {
        //                console.log("Going in submenu")
        return toggleExpandFlag(subMenu.subMenu, item);
      } else {
        //                console.log("returning as it is")
        return subMenu;
      }
    });
    return menus;
  };

  const toggleSubmenuExpand = (e, item) => {
    e.preventDefault();
    e.stopPropagation();

    // console.log("Inside toggelSubmentuExpand")
    // console.log(item)
    if (item.hasOwnProperty("subMenu")) {
      // console.log("Inside modifying flag")
      const menus = [...sideMenuOptions];
      // console.log("Menu before toggle")
      // console.log(menus)
      const modifiedMenu = toggleExpandFlag(menus, item);
      // console.log("Menu after toggle")
      // console.log(modifiedMenu)
      setSideMenuOptions(modifiedMenu);
    }
  };

  const nodeToRootPath = (node, item) => {
    // Normalize for case-insensitive match (pathname from URL is often lowercase, menu links may have mixed case)
    const nodeLinkNorm = ((node.link || "").replace(/\/$/, "") || "/").toLowerCase();
    const itemLinkNorm = ((item.link || "").replace(/\/$/, "") || "/").toLowerCase();

    // Handle exact match
    if (nodeLinkNorm === itemLinkNorm) {
      const list = [];
      list.push(node);
      return list;
    }

    // Handle pathname prefix match for leaf nodes: e.g. node link /workload-assessment/ when pathname is /workload-assessment/123
    if (nodeLinkNorm && (itemLinkNorm === nodeLinkNorm || itemLinkNorm.startsWith(nodeLinkNorm + "/"))) {
      const list = [];
      list.push(node);
      return list;
    }

    // Handle same-section match: e.g. Configuration (link /configuration/accounts) when on /configuration/users
    const nodeBase = node.link.replace(/\/[^/]+$/, "") || node.link;
    const nodeBaseNorm = nodeBase.toLowerCase();
    if (nodeBase && node.link.length > 1 && itemLinkNorm.startsWith(nodeBaseNorm + "/")) {
      const list = [];
      list.push(node);
      return list;
    }
    
    // Handle department dashboard and group-based-report routes as part of spend categorization
    if (nodeLinkNorm === "/spend-categorization" && (itemLinkNorm.startsWith("/department-dashboard") || itemLinkNorm.startsWith("/group-based-report"))) {
      const list = [];
      list.push(node);
      return list;
    }
    
    // Handle configuration suppression routes as part of configuration
    if (nodeLinkNorm === "/configuration/accounts" && itemLinkNorm.startsWith("/configuration/suppression")) {
      const list = [];
      list.push(node);
      return list;
    }
    
    // Handle all Billing Management routes as part of Billing Management menu
    if (nodeLinkNorm === "/billing-management" && itemLinkNorm.startsWith("/billing-management")) {
      const list = [];
      list.push(node);
      return list;
    }
    
    // Handle submenu items
    if (node.subMenu) {
      for (const menuItem of node.subMenu) {
        let pathTillClickedMenuItem = nodeToRootPath(menuItem, item);
        if (!pathTillClickedMenuItem) return [];
        else if (pathTillClickedMenuItem.length > 0) {
          pathTillClickedMenuItem.push(node);
          return pathTillClickedMenuItem;
        }
      }
    }
    return [];
  };

  const searchPath = (menus, item) => {
    const pathResult = [];
    let matchPath;
    for (const menuItem of menus) {
      pathResult.push(nodeToRootPath(menuItem, item));
    }
    for (const path of pathResult) {
      if (path.length > 0) {
        matchPath = path;
        break;
      }
    }
    if (!matchPath) {
      item.link = item.link.split("/").slice(0, -1).join("/") + "/";
      const pathResult = [];
      for (const menuItem of menus) {
        pathResult.push(nodeToRootPath(menuItem, item));
      }
      for (const path of pathResult) {
        if (path.length > 0) {
          matchPath = path;
          break;
        }
      }
    }
    return matchPath;
  };

  const makeLinkActive = (e, item) => {
    const menus = sidebarMenuOptions(props.t, handleRedirectLink, profile, licenseData);
    const matchPath = searchPath(menus, item);
    if (matchPath) {
      for (const pathItem of matchPath) {
        if (pathItem.subMenu) {
          pathItem.expand = true;
        } else {
          pathItem.isActive = true;
        }
      }
    }
    setSideMenuOptions(menus);
    sessionStorage.setItem("menus", JSON.stringify(menus));
  };

  useEffect(() => {
    if (!profile?.roleInfo && !profile.loading) {
      console.log("Calling getProfileRoleInfo() from SidebarContent#useEffect");
      dispatch(getProfileRoleInfo());
    }
  }, []);

  // Fetch licensing data early so it's available for SidebarMenuOptions
  useEffect(() => {
    const currentLicense = licenseData?.data?.licenseName;
    const currentLicenseLoading = licenseData?.loading;
    const currentLicenseError = licenseData?.error;
    if (!currentLicense && !currentLicenseLoading && !currentLicenseError && localStorage.getItem("authUser")) {
      dispatch(getLicensingData());
    }
  }, [dispatch, licenseData?.data?.licenseName, licenseData?.loading, licenseData?.error]);

  // Sync menu options and expand state when profile/license load (menu is always built so sidebar is never blank)
  useEffect(() => {
    const menus = sidebarMenuOptions(props.t, handleRedirectLink, profile, licenseData);
    if (menus.length === 0) return;

    const menusAsString = sessionStorage.getItem("menus");
    if (menusAsString != null && props.menuType) {
      setSideMenuOptions(JSON.parse(menusAsString));
      return;
    }
    const menuOptionsExpand = makeExpandAll(menus);
    setSideMenuOptions(menuOptionsExpand);
  }, [profile, licenseData]);

  const clearActiveExpand = (items) => {
    (items || []).forEach((it) => {
      it.isActive = false;
      it.expand = false;
      if (it.subMenu) clearActiveExpand(it.subMenu);
    });
  };

  // Sync active state with current route so highlight updates on first click and on load (including sub-pages)
  useEffect(() => {
    const menus = sidebarMenuOptions(props.t, handleRedirectLink, profile, licenseData);
    if (menus.length === 0) return;
    const pathname = (location.pathname.replace(/\/$/, "") || "/").toLowerCase();
    clearActiveExpand(menus);
    const bareLinkLinkObj = { link: pathname };
    const matchPath = searchPath(menus, bareLinkLinkObj);
    if (matchPath) {
      for (const pathItem of matchPath) {
        if (pathItem.subMenu) {
          pathItem.expand = true;
        }
        pathItem.isActive = true;
      }
      setSideMenuOptions(menus);
      sessionStorage.setItem("menus", JSON.stringify(menus));
    } else {
      // No exact/subpath match: try marking any item whose link is a path prefix of current path, or exact submenu link (case-insensitive)
      for (const menuItem of menus) {
        const base = (menuItem.link || "").replace(/\/[^/]+$/, "") || menuItem.link;
        const baseNorm = (base || "").toLowerCase();
        if (baseNorm && baseNorm !== "/" && menuItem.link.length > 1 && pathname.startsWith(baseNorm + "/")) {
          menuItem.isActive = true;
        }
        if (menuItem.subMenu) {
          for (const sub of menuItem.subMenu) {
            const subLinkNorm = ((sub.link || "").replace(/\/$/, "") || "").toLowerCase();
            const subBase = subLinkNorm.replace(/\/[^/]+$/, "") || "";
            if (subLinkNorm && pathname === subLinkNorm) {
              sub.isActive = true;
              menuItem.expand = true;
            } else if (subBase && subBase !== "/" && sub.link.length > 1 && pathname.startsWith(subBase + "/")) {
              sub.isActive = true;
              menuItem.expand = true;
            }
          }
        }
      }
      setSideMenuOptions(menus);
      sessionStorage.setItem("menus", JSON.stringify(menus));
    }
  }, [location.pathname, profile, licenseData]);

  function SidebarItem({ item, depthStep = 10, depth = 0, ...rest }) {
    if (!item.isVisible) return null;

    const isDisabled = item.isDisabled || false;
    const isTopLevelWithSubInCollapsed = isCollapsed && item.subMenu && depth === 0;

    const linkOnClick =
      isDisabled
        ? (e) => { e.preventDefault(); e.stopPropagation(); }
        : isTopLevelWithSubInCollapsed
        ? (e) => e.preventDefault()
        : item.subMenu
        ? (e) => toggleSubmenuExpand(e, item)
        : (e) => {
            e.preventDefault();
            e.stopPropagation();
            makeLinkActive(e, item);
            handleRedirectLink(e, item);
          };

    const linkEl = (
      <Link
        link={item.link}
        to={item.link && item.link !== "#" ? item.link : "#"}
        title={item.displayName}
        onClick={linkOnClick}
        group={item.group}
        className={`${item.classNames} ${item.isActive ? "active" : ""} ${isDisabled ? "disabled" : ""}`}
        style={isDisabled ? { cursor: "not-allowed" } : {}}
      >
          <Box className="d-flex flex-row justify-content-between sidebar-link-inner">
            <Box className="sidebar-icon-col">
              <Typography variant="body2">
                {item.customIcon ? (
                  <svg
                    width="25"
                    height="25"
                    viewBox="0 0 80 80"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <linearGradient x1="0%" y1="0%" x2="0%" y2="0%" id="a">
                        <stop stop-color="#fff" offset="0%" />
                        <stop stop-color="#fff" offset="0%" />
                      </linearGradient>
                    </defs>
                    <g fill="none" fill-rule="evenodd">
                      <path
                        d="M46.594 30h12.813l4.91 9h-3.75l-2.709-4.515A1 1 0 0 0 57 34h-8a1 1 0 0 0-.857.485L45.434 39h-3.749zM39.14 40.51c.181.304.508.49.86.49h6a1 1 0 0 0 .858-.485L49.568 36h6.866l2.71 4.515c.18.301.505.485.856.485h6a1 1 0 0 0 .878-1.479l-6-11A1 1 0 0 0 60 28H46a1 1 0 0 0-.878.521l-6 11a1 1 0 0 0 .018.989M59.407 54H46.594l-4.909-9h3.75l2.708 4.515c.18.301.506.485.857.485h8a1 1 0 0 0 .858-.485L60.568 45h3.748zM66 43h-6a1 1 0 0 0-.857.485L56.434 48h-6.867l-2.709-4.515A1 1 0 0 0 46 43h-6a1.002 1.002 0 0 0-.878 1.479l6 11A1 1 0 0 0 46 56h14a1 1 0 0 0 .878-.521l6-11a1 1 0 0 0-.017-.989 1 1 0 0 0-.86-.49M34.403 68H21.598l-6.45-11.98L21.573 45h12.852l6.428 11.02zm1.462-24.504A1 1 0 0 0 35 43H21c-.355 0-.684.189-.864.496l-7 12a1 1 0 0 0-.017.978l7 13c.175.324.513.526.881.526h14c.368 0 .706-.202.881-.526l7-13a1 1 0 0 0-.016-.978zM31.382 63h-6.764l-3.484-6.969L24.58 50h6.84l3.446 6.031zM32 48h-8a1 1 0 0 0-.868.504l-4 7a1 1 0 0 0-.026.943l4 8A1 1 0 0 0 24 65h8a1 1 0 0 0 .895-.553l4-8a1 1 0 0 0-.027-.943l-4-7A1 1 0 0 0 32 48M19.426 29l5.833 10h-3.685l-5.833-10zm11.956 6 2 4h-5.808l-2.333-4zm5.153-6h3.724l-5.19 8.899-1.914-3.827zM20.136 40.504c.18.307.51.496.864.496h14c.355 0 .685-.188.865-.496l7-12A1.002 1.002 0 0 0 42 27h-6c-.334 0-.646.167-.832.445L31.465 33h-7.39l-3.21-5.504A1 1 0 0 0 20 27h-6a1 1 0 0 0-.864 1.504zM21.574 13h12.852l5.833 10h-3.724l-3.703-5.555A1 1 0 0 0 32 17h-8c-.334 0-.646.167-.832.445L19.465 23h-3.724zM14 25h6a1 1 0 0 0 .832-.445L24.535 19h6.93l3.703 5.555c.186.278.498.445.832.445h6a1.002 1.002 0 0 0 .865-1.504l-7-12A1 1 0 0 0 35 11H21c-.355 0-.684.189-.864.496l-7 12A1 1 0 0 0 14 25"
                        fill="#193A6F"
                      />
                    </g>
                  </svg>
                ) : (
                  <i className={item.iconClassName}></i>
                )}

                <span className="ml-1">
                  {item.displayName}
                  {item.tag && (
                    <span style={{
                      display: 'inline-block',
                      backgroundColor: item.tagBackgroundColor,
                      color: item.tagColor,
                      padding: '1px 4px',
                      borderRadius: '10px',
                      fontSize: '0.65em',
                      marginLeft: '4px',
                      fontWeight: 500,
                      lineHeight: '1.2',
                      verticalAlign: 'super',
                      position: 'relative',
                      top: '-0.5em',
                      transform: 'scale(0.9)',
                      transformOrigin: 'left center'
                    }}>
                      {item.tag}
                    </span>
                  )}
                </span>
              </Typography>
            </Box>
            {item.subMenu && (
              <i
                className={`sidebar-submenu-chevron ${item.expand ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"}`}
                aria-hidden="true"
              />
            )}
          </Box>
      </Link>
    );

    return (
      <li
        className={`${item.isActive ? "mm-active" : ""} ${item.subMenu ? "has-submenu" : ""}`}
        key={new Date().getTime() + item.id}
        style={isDisabled ? { opacity: 0.5, filter: "blur(1px)", pointerEvents: "none" } : {}}
      >
        {isTopLevelWithSubInCollapsed ? (
          <div
            className="sidebar-collapsed-row"
            onMouseEnter={(e) => openSubmenuFlyout(item, e.currentTarget.getBoundingClientRect())}
            onMouseLeave={closeSubmenuFlyout}
          >
            {linkEl}
          </div>
        ) : (
          linkEl
        )}

        {!isCollapsed && item.expand && item.subMenu && (
          <ul
            className="metismenu list-unstyled pl-2 sidebar-submenu"
            id="side-menu"
          >
            {item.subMenu.map((entity, index) => (
              <SidebarItem
                key={`${entity.displayName}${index}`}
                depthStep={depthStep}
                depth={depth}
                item={entity}
              />
            ))}
          </ul>
        )}
      </li>
    );
  }

  // Never show empty sidebar: use state if populated, otherwise build menu on the fly so options always visible
  const allMenus =
    sideMenuOptions && sideMenuOptions.length > 0
      ? sideMenuOptions
      : makeExpandAll(sidebarMenuOptions(props.t, handleRedirectLink, profile, licenseData) || []);
  const mainMenus = (allMenus || []).filter((item) => item.group !== "Support");
  const supportMenus = (allMenus || []).filter((item) => item.group === "Support");

  return (
    <React.Fragment>
      {hoveredSubmenu &&
        createPortal(
          <div
            className="sidebar-collapsed-flyout"
            style={{
              position: "fixed",
              left: hoveredSubmenu.rect.right - 2,
              top: hoveredSubmenu.rect.top,
              zIndex: 1050,
              minWidth: 200,
            }}
            onMouseEnter={() => clearHoverTimeout()}
            onMouseLeave={closeSubmenuFlyout}
          >
            <div className="sidebar-collapsed-flyout-inner">
              {(hoveredSubmenu.item.subMenu || []).map((entity, index) =>
                entity.subMenu && entity.subMenu.length > 0 ? (
                  <div key={`${entity.displayName}-${index}`} className="sidebar-collapsed-flyout-group">
                    <div className="sidebar-collapsed-flyout-group-label">{entity.displayName}</div>
                    {entity.subMenu.map((leaf, leafIndex) => (
                      <Link
                        key={`${leaf.displayName}-${leafIndex}`}
                        to={leaf.link}
                        className={`sidebar-collapsed-flyout-item ${isPathActive(leaf.link) ? "active" : ""}`}
                        onClick={(e) => handleSubmenuLinkClick(e, leaf)}
                      >
                        {leaf.displayName}
                      </Link>
                    ))}
                  </div>
                ) : (
                  <Link
                    key={`${entity.displayName}-${index}`}
                    to={entity.link}
                    className={`sidebar-collapsed-flyout-item ${isPathActive(entity.link) ? "active" : ""}`}
                    onClick={(e) => handleSubmenuLinkClick(e, entity)}
                  >
                    {entity.displayName}
                  </Link>
                )
              )}
            </div>
          </div>,
          document.body
        )}
      <DirtyRedirectCheckHoc isParentElement={true}>
        <Box id="sidebar-menu" className="sidebar-menu-inner sidebar-menu-with-support">
          <ul className="metismenu list-unstyled" id="side-menu">
            {mainMenus.map((item, index) => (
              <SidebarItem
                key={`${item.displayName}${index}`}
                depthStep={depthStep}
                depth={depth}
                item={item}
              />
            ))}
          </ul>
          {supportMenus.length > 0 && (
            <div className="sidebar-support-section">
              <ul className="metismenu list-unstyled" id="side-menu-support">
                {supportMenus.map((item, index) => (
                  <SidebarItem
                    key={`${item.displayName}${index}`}
                    depthStep={depthStep}
                    depth={depth}
                    item={item}
                  />
                ))}
              </ul>
            </div>
          )}
        </Box>
      </DirtyRedirectCheckHoc>
    </React.Fragment>
  );
};

export default withNamespaces()(SidebarContent);
